import { subject } from '@casl/ability';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonIcon,
  IonRow,
  IonText,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { cloudDownloadOutline, optionsOutline } from 'ionicons/icons';
import { ReactElement, memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useHasPermission } from '../../api/authorization.provider';
import {
  Chart,
  PlanEntitlementKey,
  useGetChartDataQuery,
  useListChartsQuery,
} from '../../api/graphql/generated';
import { useDownloadChartLazy } from '../../api/hooks/chart.hooks';
import { useBeaconRouter } from '../../route/route.hooks';
import { usePrevious } from '../../util/hooks';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { PaywallIonButton } from '../paywall/paywall-ion-button';
import { BeaconChart, BeaconChartRef } from './beacon-chart';
import { orderCharts } from './chart-utils';

interface ChartListProps {
  parentId: string;
}

const ChartList = (props: ChartListProps): ReactElement => {
  const { parentId } = props;
  const { i18n } = useTranslation();

  const canCreateChart = useHasPermission('create', 'chart', { parentId });

  const { data, loading, error, refetch } = useListChartsQuery({
    variables: {
      input: {
        parentId,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { builder } = useBeaconRouter();

  const charts = orderCharts(data?.listCharts.items);

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: data?.listCharts.total !== 0,
        noResultsComponent: (
          <div className="full-height-width content-center">
            <IonText className="ion-padding ion-text-center" color="medium">
              <h2>
                {canCreateChart
                  ? i18n.t('common:chart_create_first_chart')
                  : i18n.t('common:chart_no_charts_available')}
              </h2>
            </IonText>
          </div>
        ),
      }}
    >
      <IonGrid>
        <IonRow>
          {charts.map((chart) => {
            return (
              <IonCol key={chart.id} sizeXs={'12'} sizeLg={'6'}>
                <ChartCard
                  chart={chart}
                  editUrl={builder.getTeamChartRoute(chart.parentId, chart.id, {
                    queryParams: {
                      templateId: chart.templateId,
                    },
                  })}
                />
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
      <div style={{ height: '100px' }} />
    </LoadingRetry>
  );
};

interface ChartCardProps {
  chart: Partial<Chart>;
  editUrl: string;
}

const ChartCard = memo((props: ChartCardProps): ReactElement => {
  const { chart, editUrl } = props;
  const { i18n } = useTranslation();

  const canExportChart = useHasPermission('export', 'chart', chart);

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useGetChartDataQuery({
      variables: {
        input: {
          id: chart.id as string,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: chart.id === undefined,
    });

  useIonViewWillEnter(() => {
    startPolling(15000);
  });

  useIonViewWillLeave(() => {
    stopPolling();
  });

  const chartData = data?.getChartData;
  const beaconChartRef = useRef<BeaconChartRef>(null);
  const prevFields = usePrevious(JSON.stringify(chart.fields));

  useEffect(() => {
    if (
      prevFields !== null &&
      JSON.stringify(chart.fields) !== prevFields &&
      beaconChartRef.current
    ) {
      beaconChartRef.current.clearChart();
    }
  }, [chart.fields, prevFields]);

  const downloadChart = useDownloadChartLazy();
  const handleDownload = useCallback(() => {
    if (chart.id) {
      downloadChart(chart.id);
    }
  }, [chart.id, downloadChart]);

  return (
    <IonCard>
      <div className="chart-card-grid">
        <IonCardHeader className="chart-title-container">
          <IonCardTitle className="chart-title">{chart.title}</IonCardTitle>
        </IonCardHeader>
        <IonButtons className="chart-buttons">
          <Can I="update" this={subject('chart', { parentId: chart.parentId })}>
            <IonButton routerLink={editUrl}>
              <IonIcon icon={optionsOutline} />
            </IonButton>
          </Can>
          {chart.id !== undefined && (
            <PaywallIonButton
              planEntitlementKey={PlanEntitlementKey.CHART_EXPORT}
              isFeatureAvailable={() => canExportChart}
              ionButtonProps={{
                onClick: handleDownload,
              }}
            >
              <IonIcon icon={cloudDownloadOutline} />
            </PaywallIonButton>
          )}
        </IonButtons>
        <IonCardContent className="chart-container">
          <LoadingRetry
            loading={loading}
            error={error}
            refetch={() => {
              refetch();
            }}
            results={{ hasResults: true, noResultsComponent: <div /> }}
          >
            <BeaconChart
              ref={beaconChartRef}
              data={chartData}
              chart={chart}
              renderNoResults={
                <div className="full-height-width content-center">
                  <h2>
                    {i18n.t('common:chart_no_chart_results')}
                    <span className="ion-padding" role="img" aria-label="sad">
                      😞
                    </span>
                  </h2>
                </div>
              }
            />
          </LoadingRetry>
        </IonCardContent>
      </div>
    </IonCard>
  );
});

ChartCard.displayName = 'ChartCard';

export { ChartList };
